// constants
import SideNavItems, {
    SideNavType,
} from '../../constants/SideNavItems.constant';
import { FormulaSequenceStatus } from '../../constants/sequence';

// helpers
import { canAccessChannelOverrides } from '../../helpers/channelDefinitionHelpers';

import { FormulaStatusCountModel } from '../../models/formula-sequence.model';
// models
import {
    FormulaCategory,
    FormulasCategoryCountModel,
    FormulaChannelCountModel,
} from './../../models/formula.model';

import { SideNavId, SideNavItem } from './SideNavItem.type';

const sumChannelCounts = (counts: FormulaChannelCountModel): number => {
    return Object.values(counts).reduce((acc, count) => acc + count, 0);
};
const AAMPE_CUSTOMER_ID = 1001;

export const mapSideNavItems = (
    formulasCount: FormulasCategoryCountModel | undefined,
    formulasSequencesCount: FormulaStatusCountModel | undefined,
    isInternalAampeAdmin?: boolean,
    customerId?: number,
    uncategorisedEventsCount?: number,
    isBetaUser?: boolean,
): SideNavType[] => {
    const canAccessAampeAdmin =
        isInternalAampeAdmin && customerId === AAMPE_CUSTOMER_ID;
    const filteredItems = canAccessAampeAdmin
        ? SideNavItems
        : SideNavItems.filter((item) => item.item.id !== SideNavId.AampeAdmin);

    return filteredItems.map((item: SideNavType) => {
        let subItems = item.subItems;

        if (item.item.id === SideNavId.Monitoring && !isBetaUser) {
            subItems = subItems?.filter(
                (subItem) => subItem.id !== SideNavId.Assignments,
            );
        }

        if (formulasCount && item.item.id === SideNavId.Messages) {
            subItems = subItems?.map((subitem: SideNavItem) => {
                switch (subitem.id) {
                    case SideNavId.MessagesDrafts:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${sumChannelCounts(
                                formulasCount[FormulaCategory.Draft],
                            )})`,
                        };
                    case SideNavId.MessagesReview:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${sumChannelCounts(
                                formulasCount[FormulaCategory.Review],
                            )})`,
                        };
                    case SideNavId.MessageScheduled:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${sumChannelCounts(
                                formulasCount[FormulaCategory.Scheduled],
                            )})`,
                        };
                    case SideNavId.MessageLive:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${sumChannelCounts(
                                formulasCount[FormulaCategory.Live],
                            )})`,
                        };
                    case SideNavId.MessageExpired:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${sumChannelCounts(
                                formulasCount[FormulaCategory.Expired],
                            )})`,
                        };
                    case SideNavId.MessagesArchived:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${sumChannelCounts(
                                formulasCount[FormulaCategory.Archived],
                            )})`,
                        };
                    default:
                        return subitem;
                }
            });
        }

        if (
            formulasSequencesCount &&
            item.item.id === SideNavId.FormulaSequences
        ) {
            subItems = subItems?.map((subitem: SideNavItem) => {
                switch (subitem.id) {
                    case SideNavId.FormulaSequencesLive:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${
                                formulasSequencesCount[
                                    FormulaSequenceStatus.Live
                                ]
                            })`,
                        };
                    case SideNavId.FormulaSequencesDrafts:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${
                                formulasSequencesCount[
                                    FormulaSequenceStatus.Draft
                                ]
                            })`,
                        };
                    case SideNavId.FormulaSequencesArchived:
                        return {
                            ...subitem,
                            text: `${subitem.text} (${
                                formulasSequencesCount[
                                    FormulaSequenceStatus.Archived
                                ]
                            })`,
                        };
                    default:
                        return subitem;
                }
            });
        }

        if (
            item.item.id === SideNavId.SystemSetup &&
            uncategorisedEventsCount &&
            uncategorisedEventsCount > 0
        ) {
            subItems = subItems?.map((subItem) => {
                if (subItem.id === SideNavId.Events) {
                    return {
                        ...subItem,
                        hasWarning: true,
                        warningTooltip: `${uncategorisedEventsCount} Events don’t have a type set`,
                    };
                }
                return subItem;
            });
        }

        // Pilot-rollout gate: only customers on the allowlist should see the
        // "Channels" nav entry - see canAccessChannelOverrides.
        if (
            item.item.id === SideNavId.SystemSetup &&
            !canAccessChannelOverrides(customerId)
        ) {
            subItems = subItems?.filter(
                (subItem) => subItem.id !== SideNavId.Channels,
            );
        }

        return { ...item, subItems };
    });
};
